import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'data-fetching-client';
import getIn from 'immutable-less/transmute/getIn';
import isEmpty from 'transmute/isEmpty';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIForm from 'UIComponents/form/UIForm';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIModal from 'UIComponents/dialog/UIModal';
import UITextArea from 'UIComponents/input/UITextArea';
import UITextInput from 'UIComponents/input/UITextInput';
import { useTrackEvent } from 'automation-ui-usage-tracking/usageTrackingHooks';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import * as FlowUtils from 'automation-ui-flow-core/flow/flowUtils';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import GenerateDescriptionButton from 'automation-ui-management/components/WorkflowNameWithDescription/GenerateDescriptionButton';
import GenericErrorMessage from 'automation-ui-management/components/errors/GenericErrorMessage';
import { useGetNameAndDescription, useUpdateNameAndDescription } from 'automation-ui-management/components/WorkflowNameWithDescription/nameAndDescriptionHooks';
import GenerateDescriptionErrorMessage from 'automation-ui-management/components/WorkflowNameWithDescription/GenerateDescriptionErrorMessage';
import { FLOW_TYPES } from 'automation-ui-management/constants/CrmPropertyEnums';
import { GENERATE_DESCRIPTION, GENERATE_DESCRIPTION_FIELD_NAME, RATE_DESCRIPTION } from 'automation-ui-management/queries/flowQueries';
import { GET_AI_SETTINGS_ACCESS } from 'automation-ui-management/queries/aiSettingsQueries';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EditWorkflowNameAndDescriptionModal = ({
  flow: flowFromProps,
  flowId,
  onClose,
  onDescriptionSaveSuccess,
  onNameSaveSuccess,
  readOnly,
  headerLabel,
  initialGeneratedDescription,
  initialGeneratedName
}) => {
  var _data$aiSettingsAcces, _data$aiSettingsAcces2;
  const trackEvent = useTrackEvent();
  const {
    data,
    loading: aiSettingsAccessLoading
  } = useQuery(GET_AI_SETTINGS_ACCESS);
  const isContentAssistanceEnabled = (_data$aiSettingsAcces = data === null || data === void 0 || (_data$aiSettingsAcces2 = data.aiSettingsAccess) === null || _data$aiSettingsAcces2 === void 0 ? void 0 : _data$aiSettingsAcces2.crmPropertiesEnabled) !== null && _data$aiSettingsAcces !== void 0 ? _data$aiSettingsAcces : false;
  const {
    flow,
    description,
    getCrmObjectResult,
    getFlowQueryResult,
    shouldPollForDescription
  } = useGetNameAndDescription({
    flowId,
    flowFromProps
  });
  const {
    updateCrmObject,
    updateCrmObjectQueryResult,
    updateFlow,
    updateFlowQueryResult
  } = useUpdateNameAndDescription({
    flowId,
    onNameSaveSuccess,
    onDescriptionSaveSuccess
  });
  const flowName = flow && flow.name ? flow.name : '';
  const [currentName, setCurrentName] = useState(initialGeneratedName || flowName);
  const [currentDescription, setCurrentDescription] = useState(initialGeneratedDescription || description);
  const isFlowEmpty = FlowUtils.isFlowEmpty(flow);
  const [descriptionRating, setDescriptionRating] = useState();
  const [descriptionFeedback, setDescriptionFeedback] = useState();
  const [hasSubmittedFeedback, setHasSubmittedFeedback] = useState(false);

  /* initialize name if flow loads after modal */
  useEffect(() => {
    if (!initialGeneratedName) {
      setCurrentName(flowName);
    }
  }, [flowName, initialGeneratedName]);
  const [generateDescriptionErrorSubcategory, setGenerateDescriptionErrorSubcategory] = useState();
  const [generateDescription, generateDescriptionResult] = useMutation(GENERATE_DESCRIPTION, {
    onError: error => {
      const errorSubCategory = getIn(['networkError', 'responseJSON', 'subCategory'], error);
      setGenerateDescriptionErrorSubcategory(errorSubCategory);
      reportError({
        error
      });
    }
  });
  const generatedDescription = getIn(['data', GENERATE_DESCRIPTION_FIELD_NAME, 'description'], generateDescriptionResult);
  const descriptionId = getIn(['data', GENERATE_DESCRIPTION_FIELD_NAME, 'id'], generateDescriptionResult);
  const hasGeneratedDescription = !!generatedDescription || !!initialGeneratedDescription;
  const hasGeneratedName = !!initialGeneratedName;
  const [rateDescription] = useMutation(RATE_DESCRIPTION, {
    onCompleted: () => {
      if (descriptionId && descriptionRating) {
        trackEvent(Trackables.RATE_DESCRIPTION_WORKFLOW_NAME_AND_DESCRIPTION_MODAL, {
          description: generatedDescription,
          feedback: descriptionFeedback,
          flowId,
          rating: descriptionRating
        });
      }
      setHasSubmittedFeedback(true);
    },
    onError: error => {
      reportError({
        error
      });
    }
  });
  useEffect(() => {
    // if we're polling for description, we need to update local
    // state to reflect the correct description when it comes back
    if (!initialGeneratedDescription) {
      setCurrentDescription(description);
    }
  }, [description, initialGeneratedDescription]);
  useEffect(() => {
    if (generatedDescription) {
      setCurrentDescription(generatedDescription);
    }
  }, [generatedDescription]);
  const hasChanges = currentName !== flowName || currentDescription !== description;
  const isNameEmpty = isEmpty(currentName);
  const handleNameChange = event => {
    const {
      value
    } = event.currentTarget;
    setCurrentName(value);
  };
  const handleDescriptionChange = event => {
    const {
      value
    } = event.currentTarget;
    setCurrentDescription(value);
  };
  const trackNameAndDescriptionChanges = () => {
    const extraJSONData = {
      description: description !== currentDescription,
      name: flowName !== currentName,
      wasGeneratedDescriptionCustomized: !!generatedDescription && currentDescription !== generatedDescription || !!initialGeneratedDescription && currentDescription !== initialGeneratedDescription,
      withGeneratedDescription: hasGeneratedDescription
    };
    trackEvent(Trackables.SAVED_WORKFLOW_NAME_AND_DESCRIPTION_MODAL, extraJSONData);
  };
  const handleGenerateDescription = () => {
    generateDescription({
      variables: {
        flowId
      }
    }).then(() => {
      trackEvent(generatedDescription ? Trackables.REGENERATE_DESCRIPTION_WORKFLOW_NAME_AND_DESCRIPTION_MODAL : Trackables.GENERATE_DESCRIPTION_WORKFLOW_NAME_AND_DESCRIPTION_MODAL);
      setDescriptionRating(undefined);
      setDescriptionFeedback(undefined);
      setHasSubmittedFeedback(false);
    }).catch(error => {
      reportError({
        error
      });
    });
  };
  const handleSubmitRateDescription = () => {
    if (descriptionId && descriptionRating) {
      // intentionally avoiding making an error with this query visible
      // to the user, we don't want to produce any friction to the changes
      // related to not being able to save the rating
      rateDescription({
        variables: {
          descriptionId,
          rating: descriptionRating,
          feedback: descriptionFeedback
        }
      }).catch(error => {
        reportError({
          error
        });
      });
    }
  };
  const handleCancel = () => {
    trackEvent(Trackables.CLOSED_WORKFLOW_NAME_AND_DESCRIPTION_MODAL);
    onClose();
  };
  const handleSave = e => {
    e.preventDefault();
    const queries = [];
    if (currentName && flowName !== currentName) {
      const newFlow = Object.assign({}, flow, {
        name: currentName
      });
      const updateFlowQuery = updateFlow({
        variables: {
          flow: newFlow,
          sourceApp: SourceApps.WORKFLOWS_APP
        }
      });
      queries.push(updateFlowQuery);
    }
    if (description !== currentDescription) {
      const updateCrmQuery = updateCrmObject({
        variables: {
          flowId,
          description: currentDescription || ''
        }
      });
      queries.push(updateCrmQuery);
    }
    Promise.all(queries).then(() => {
      trackNameAndDescriptionChanges();
      onClose();
    })
    // no need to do anything here since we use error
    // from the mutation queries to render an error message
    .catch(error => reportError({
      error
    }));
  };
  const hasDataLoaded = !getFlowQueryResult.loading && !getCrmObjectResult.loading && !shouldPollForDescription;
  const flowType = getIn(['data', 'automation_ui_index_crmObjectSearch', 'properties', 'hs_flow_type', 'value'], getCrmObjectResult);
  const isActionSet = flowType === FLOW_TYPES.ACTION_SET;
  const getDescriptionPlaceholder = () => {
    if (isActionSet && !isContentAssistanceEnabled) {
      return I18n.text('automation-ui-management.WorkflowNameWithDescription.modal.descriptionPlaceholder.actionSet');
    }
    if (isActionSet && isContentAssistanceEnabled) {
      return I18n.text('automation-ui-management.WorkflowNameWithDescription.modal.descriptionPlaceholderAI.actionSet');
    }
    if (isContentAssistanceEnabled) {
      return I18n.text('automation-ui-management.WorkflowNameWithDescription.modal.descriptionPlaceholderAI.workflow');
    }
    return I18n.text('automation-ui-management.WorkflowNameWithDescription.modal.descriptionPlaceholder.workflow');
  };
  const defaultLabel = isActionSet ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "automation-ui-management.WorkflowNameWithDescription.modal.header.actionSet"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "automation-ui-management.WorkflowNameWithDescription.modal.header.workflow"
  });
  return /*#__PURE__*/_jsxs(UIModal, {
    width: 500,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: headerLabel || defaultLabel
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: handleCancel
      })]
    }), getFlowQueryResult.error || getCrmObjectResult.error ? /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(GenericErrorMessage, {
        title: isActionSet ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.WorkflowNameWithDescription.modal.loadingError.title.actionSet"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.WorkflowNameWithDescription.modal.loadingError.title.workflow"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-management.WorkflowNameWithDescription.modal.loadingError.body"
        })
      })
    }) : /*#__PURE__*/_jsxs(UIForm, {
      onSubmit: handleSave,
      children: [/*#__PURE__*/_jsxs(UIDialogBody, {
        children: [(updateCrmObjectQueryResult.error || updateFlowQueryResult.error) && /*#__PURE__*/_jsx(UIAlert, {
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.WorkflowNameWithDescription.modal.savingError.title"
          }),
          type: "danger",
          children: isActionSet ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.WorkflowNameWithDescription.modal.savingError.body.actionSet"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.WorkflowNameWithDescription.modal.savingError.body.workflow"
          })
        }), generateDescriptionResult.error && /*#__PURE__*/_jsx(GenerateDescriptionErrorMessage, {
          error: generateDescriptionErrorSubcategory,
          flowType: flowType
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(Flex, {
              justifyContent: "start",
              inlineSize: "388px",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-management.WorkflowNameWithDescription.modal.nameLabel"
              })
            }), hasGeneratedName && /*#__PURE__*/_jsx(AIBadge, {})]
          }),
          children: /*#__PURE__*/_jsx(UITextInput, {
            autoFocus: true,
            error: isNameEmpty,
            onChange: handleNameChange,
            value: currentName,
            disabled: !hasDataLoaded,
            readOnly: readOnly
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsxs(Flex, {
            children: [/*#__PURE__*/_jsx(Flex, {
              justifyContent: "start",
              inlineSize: "388px",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "automation-ui-management.WorkflowNameWithDescription.modal.descriptionLabel"
              })
            }), hasGeneratedDescription && /*#__PURE__*/_jsx(AIBadge, {})]
          }),
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(UITextArea
          // adjusting height of the textarea when the generated description rating form is displayed so the height
          // of the modal does not change
          , {
            rows: descriptionRating && !hasSubmittedFeedback ? 3 : 10,
            placeholder: getDescriptionPlaceholder(),
            onChange: handleDescriptionChange,
            value: currentDescription || '',
            disabled: !hasDataLoaded,
            readOnly: readOnly
          })
        }), /*#__PURE__*/_jsx(GenerateDescriptionButton, {
          descriptionFeedback: descriptionFeedback,
          descriptionRating: descriptionRating,
          disabled: !hasDataLoaded || aiSettingsAccessLoading || !isContentAssistanceEnabled || isFlowEmpty,
          flowType: flowType,
          generatedDescription: generatedDescription,
          generateDescriptionError: !!generateDescriptionResult.error,
          generateDescriptionLoading: generateDescriptionResult.loading,
          isContentAssistanceEnabled: isContentAssistanceEnabled,
          isFlowEmpty: isFlowEmpty,
          hasSubmittedFeedback: hasSubmittedFeedback,
          onGenerateDescription: handleGenerateDescription,
          onRateDescription: ({
            rating,
            feedback
          }) => {
            setDescriptionRating(rating);
            setDescriptionFeedback(feedback);
          },
          onSubmitRateDescription: handleSubmitRateDescription
        })]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsx(UILoadingButton, {
          failed: Boolean(updateCrmObjectQueryResult.error || updateFlowQueryResult.error),
          loading: updateCrmObjectQueryResult.loading || updateFlowQueryResult.loading,
          use: "primary",
          type: "submit",
          disabled: isNameEmpty || readOnly || !hasDataLoaded || !hasChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.WorkflowNameWithDescription.modal.saveButton"
          })
        }), /*#__PURE__*/_jsx(UIButton, {
          onClick: handleCancel,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-management.WorkflowNameWithDescription.modal.cancelButton"
          })
        })]
      })]
    })]
  });
};
export default EditWorkflowNameAndDescriptionModal;